import React, {useEffect, useRef, useState} from 'react';
import styles from './AdminUploadFilters.module.css';

export const UPLOAD_FILTER_KEYS = [
    {key: 'robotArms', optionsKey: 'robot_arms', label: 'Robot arm', placeholder: 'Search robot arms…'},
    {key: 'operators', optionsKey: 'operators', label: 'Operators', placeholder: 'Search operators…'},
    {key: 'tasks', optionsKey: 'tasks', label: 'Tasks', placeholder: 'Search tasks…'},
    {key: 'jobs', optionsKey: 'jobs', label: 'Jobs', placeholder: 'Search jobs…'},
];

export const EMPTY_UPLOAD_FILTER_SELECTIONS = {robotArms: [], operators: [], tasks: [], jobs: []};

const DATE_PRESETS = [
    {value: 'today', label: 'Today'},
    {value: 'last7', label: 'Last 7 days'},
    {value: 'last30', label: 'Last 30 days'},
    {value: 'thisMonth', label: 'This month'},
    {value: 'all', label: 'All time'},
    {value: 'custom', label: 'Custom range…'},
];

function startOfLocalDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date, days) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function parseLocalDate(value) {
    const [year, month, day] = String(value || '').split('-').map(Number);
    return year && month && day ? new Date(year, month - 1, day) : null;
}

// Returns {preset, from, to, createdFrom, createdTo}; createdTo is exclusive (start of the next local day).
export function resolveUploadDateRange(preset, from = '', to = '', now = new Date()) {
    const today = startOfLocalDay(now);
    let start = null;
    let end = null;
    if (preset === 'today') [start, end] = [today, addDays(today, 1)];
    else if (preset === 'last7') [start, end] = [addDays(today, -6), addDays(today, 1)];
    else if (preset === 'last30') [start, end] = [addDays(today, -29), addDays(today, 1)];
    else if (preset === 'thisMonth') [start, end] = [new Date(today.getFullYear(), today.getMonth(), 1), addDays(today, 1)];
    else if (preset === 'custom') {
        const fromDate = parseLocalDate(from);
        const toDate = parseLocalDate(to);
        if (fromDate && toDate && fromDate <= toDate) [start, end] = [fromDate, addDays(toDate, 1)];
    }
    if (!start) return null;
    return {preset, from, to, createdFrom: start.toISOString(), createdTo: end.toISOString()};
}

function CheckBox({checked}) {
    return (
        <span className={`${styles.checkbox} ${checked ? styles.checkboxChecked : ''}`} aria-hidden="true">
            {checked ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
            ) : null}
        </span>
    );
}

function ChevronIcon() {
    return (
        <svg className={styles.chevron} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polyline points="6 9 12 15 18 9"/>
        </svg>
    );
}

function ClearIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
    );
}

function SearchIcon() {
    return (
        <svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
    );
}

function useDismiss(open, onDismiss) {
    const ref = useRef(null);
    const onDismissRef = useRef(onDismiss);
    onDismissRef.current = onDismiss;
    useEffect(() => {
        if (!open) return undefined;
        const handleMouseDown = (event) => {
            if (ref.current && !ref.current.contains(event.target)) onDismissRef.current();
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') onDismissRef.current();
        };
        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);
    return ref;
}

function TriggerButton({label, active, text, count, open, onToggle, onClear, icon}) {
    return (
        <div className={`${styles.pill} ${styles.trigger} ${active ? styles.pillActive : ''}`}>
            <button type="button" className={styles.triggerMain} onClick={onToggle} aria-haspopup="true" aria-expanded={open}>
                {icon}
                <span className={styles.triggerText}>{text}</span>
                {count > 1 ? <span className={styles.count}>{count}</span> : null}
                {!active ? <ChevronIcon/> : null}
            </button>
            {active ? (
                <button type="button" className={styles.triggerClear} onClick={onClear} aria-label={`Clear ${label} filter`}>
                    <ClearIcon/>
                </button>
            ) : null}
        </div>
    );
}

function MultiSelectDropdown({label, placeholder, options, selected, onChange, open, onOpenChange}) {
    const [query, setQuery] = useState('');
    const inputRef = useRef(null);
    const ref = useDismiss(open, () => onOpenChange(false));
    const selectedSet = new Set(selected.map(String));
    const normalizedQuery = query.trim().toLowerCase();
    const visibleOptions = options.filter((option) => (
        !normalizedQuery || `${option.label} ${option.meta || ''} ${option.keywords || ''}`.toLowerCase().includes(normalizedQuery)
    ));
    const onlySelected = selected.length === 1
        ? options.find((option) => String(option.value) === String(selected[0]))
        : null;

    useEffect(() => {
        if (!open) return;
        setQuery('');
        inputRef.current?.focus();
    }, [open]);

    const toggleValue = (value) => {
        const key = String(value);
        onChange(selectedSet.has(key)
            ? selected.filter((item) => String(item) !== key)
            : [...selected, value]);
    };

    return (
        <div className={styles.dropdown} ref={ref}>
            <TriggerButton
                label={label}
                active={selected.length > 0}
                text={onlySelected ? onlySelected.label : label}
                count={selected.length}
                open={open}
                onToggle={() => onOpenChange(!open)}
                onClear={() => onChange([])}
            />
            {open ? (
                <div className={styles.panel} role="dialog" aria-label={`${label} filter`}>
                    <div className={styles.panelSearch}>
                        <SearchIcon/>
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder={placeholder}
                            aria-label={placeholder}
                        />
                        {query ? (
                            <button type="button" className={styles.clearQuery} onClick={() => { setQuery(''); inputRef.current?.focus(); }} aria-label="Clear search">
                                <ClearIcon/>
                            </button>
                        ) : null}
                    </div>
                    <div className={styles.options} role="listbox" aria-multiselectable="true" aria-label={`${label} options`}>
                        {visibleOptions.map((option) => {
                            const isSelected = selectedSet.has(String(option.value));
                            return (
                                <div
                                    key={String(option.value)}
                                    role="option"
                                    aria-selected={isSelected}
                                    tabIndex={0}
                                    className={`${styles.option} ${isSelected ? styles.optionSelected : ''}`}
                                    onClick={() => toggleValue(option.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            toggleValue(option.value);
                                        }
                                    }}
                                >
                                    <CheckBox checked={isSelected}/>
                                    <span className={styles.optionLabel}>{option.label}</span>
                                    {option.meta ? <span className={styles.optionMeta}>{option.meta}</span> : null}
                                </div>
                            );
                        })}
                        {!visibleOptions.length ? (
                            <div className={styles.empty}>
                                {options.length ? `No matches for "${query}"` : 'No options available'}
                            </div>
                        ) : null}
                    </div>
                    {selected.length ? (
                        <button type="button" className={styles.panelFooter} onClick={() => onChange([])}>
                            Clear selection ({selected.length})
                        </button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

function DateDropdown({value, onChange, open, onOpenChange}) {
    const [fromInput, setFromInput] = useState(value?.from || '');
    const [toInput, setToInput] = useState(value?.to || '');
    const [showCustom, setShowCustom] = useState(value?.preset === 'custom');
    const ref = useDismiss(open, () => onOpenChange(false));
    const formatShort = (text) => parseLocalDate(text)?.toLocaleDateString('en-US', {month: 'short', day: 'numeric'}) || text;
    const canApply = Boolean(fromInput && toInput && fromInput <= toInput);

    useEffect(() => {
        if (!open) return;
        setFromInput(value?.from || '');
        setToInput(value?.to || '');
        setShowCustom(value?.preset === 'custom');
    }, [open, value]);

    const text = !value ? 'Date'
        : value.preset === 'custom' ? `${formatShort(value.from)} – ${formatShort(value.to)}`
            : DATE_PRESETS.find((preset) => preset.value === value.preset)?.label || 'Date';

    const selectPreset = (preset) => {
        if (preset === 'custom') {
            setShowCustom(true);
            return;
        }
        onChange(preset === 'all' ? null : resolveUploadDateRange(preset));
        onOpenChange(false);
    };

    return (
        <div className={styles.dropdown} ref={ref}>
            <TriggerButton
                label="Date"
                active={Boolean(value)}
                text={text}
                count={0}
                open={open}
                onToggle={() => onOpenChange(!open)}
                onClear={() => onChange(null)}
                icon={(
                    <svg className={styles.calendarIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                )}
            />
            {open ? (
                <div className={`${styles.panel} ${styles.datePanel}`} role="dialog" aria-label="Date filter">
                    {DATE_PRESETS.map((preset) => {
                        const isSelected = preset.value === 'custom'
                            ? showCustom
                            : !showCustom && (value?.preset || 'all') === preset.value;
                        return (
                            <button
                                type="button"
                                key={preset.value}
                                className={`${styles.datePreset} ${isSelected ? styles.optionSelected : ''}`}
                                onClick={() => selectPreset(preset.value)}
                                aria-pressed={isSelected}
                            >
                                {preset.label}
                            </button>
                        );
                    })}
                    {showCustom ? (
                        <div className={styles.dateCustom}>
                            <label>
                                From
                                <input type="date" value={fromInput} onChange={(event) => setFromInput(event.target.value)}/>
                            </label>
                            <label>
                                To
                                <input type="date" value={toInput} onChange={(event) => setToInput(event.target.value)}/>
                            </label>
                            <button
                                type="button"
                                className={styles.dateApply}
                                disabled={!canApply}
                                onClick={() => {
                                    onChange(resolveUploadDateRange('custom', fromInput, toInput));
                                    onOpenChange(false);
                                }}
                            >
                                Apply
                            </button>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

export default function AdminUploadFilters({
    options,
    includeTestUploads,
    onIncludeTestUploadsChange,
    failedOnly,
    onFailedOnlyChange,
    publicOnly,
    onPublicOnlyChange,
    qaRejectedOnly = false,
    onQaRejectedOnlyChange,
    selections,
    onSelectionChange,
    dateRange,
    onDateRangeChange,
    onReset,
}) {
    const [openDropdown, setOpenDropdown] = useState(null);
    const testTaskId = options?.test_upload_task_id;
    const activeCount = UPLOAD_FILTER_KEYS.reduce((total, filter) => total + selections[filter.key].length, 0)
        + (dateRange ? 1 : 0);
    const setOpen = (key) => (isOpen) => setOpenDropdown((current) => (isOpen ? key : (current === key ? null : current)));

    return (
        <div className={styles.filterBar}>
            <span className={styles.filterLabel}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <line x1="4" y1="8" x2="20" y2="8"/><line x1="7" y1="14" x2="17" y2="14"/><line x1="10" y1="20" x2="14" y2="20"/>
                </svg>
                Filters
            </span>
            <button
                type="button"
                className={`${styles.pill} ${includeTestUploads ? styles.pillActive : ''}`}
                onClick={() => onIncludeTestUploadsChange(!includeTestUploads)}
                aria-pressed={includeTestUploads}
            >
                <CheckBox checked={includeTestUploads}/>
                Include test uploads
            </button>
            <button
                type="button"
                className={`${styles.pill} ${failedOnly ? styles.pillDanger : ''}`}
                onClick={() => onFailedOnlyChange(!failedOnly)}
                aria-pressed={failedOnly}
            >
                <CheckBox checked={failedOnly}/>
                Failed only
            </button>
            {onQaRejectedOnlyChange && <button
                type="button"
                className={`${styles.pill} ${qaRejectedOnly ? styles.pillDanger : ''}`}
                onClick={() => onQaRejectedOnlyChange(!qaRejectedOnly)}
                aria-pressed={qaRejectedOnly}
            >
                <CheckBox checked={qaRejectedOnly}/>
                QA rejected
            </button>}
            <button
                type="button"
                className={`${styles.pill} ${publicOnly ? styles.pillActive : ''}`}
                onClick={() => onPublicOnlyChange(!publicOnly)}
                aria-pressed={publicOnly}
            >
                <CheckBox checked={publicOnly}/>
                Public catalog only
            </button>
            {UPLOAD_FILTER_KEYS.map((filter) => {
                let filterOptions = Array.isArray(options?.[filter.optionsKey]) ? options[filter.optionsKey] : [];
                if (filter.key === 'tasks' && !includeTestUploads && testTaskId) {
                    filterOptions = filterOptions.filter((option) => Number(option.value) !== Number(testTaskId));
                }
                return (
                    <MultiSelectDropdown
                        key={filter.key}
                        label={filter.label}
                        placeholder={filter.placeholder}
                        options={filterOptions}
                        selected={selections[filter.key]}
                        onChange={(values) => onSelectionChange(filter.key, values)}
                        open={openDropdown === filter.key}
                        onOpenChange={setOpen(filter.key)}
                    />
                );
            })}
            <DateDropdown value={dateRange} onChange={onDateRangeChange} open={openDropdown === 'date'} onOpenChange={setOpen('date')}/>
            {activeCount > 0 ? (
                <button type="button" className={styles.resetFilters} onClick={onReset}>
                    Reset filters ({activeCount})
                </button>
            ) : null}
        </div>
    );
}
